import {
    ArcType,
    CallbackProperty,
    Cartesian2,
    Cartesian3,
    Cartographic,
    ClassificationType,
    Color,
    Ellipsoid,
    Entity,
    HeightReference,
    LabelStyle,
    PolygonHierarchy,
    ScreenSpaceEventType,
} from 'cesium';

/**
 * 测量
 */
export class Measure {
    constructor(viewer, options = {}) {
        this.viewer = viewer;
        this.entityCollection = [];
        this.viewer.scene.globe.depthTestAgainstTerrain = true;
        this.startDraw = false;
        this.startTipEntity = null;
        this.labelEntityCollection = [];
        this.totalEntityCollection = [];
        this.clearLast = true;
        this.labelVisible = true;
        this.TextEnum = options.TextEnum;
    }

    // var this.entityCollection = [];

    getCollection() {
        return this.entityCollection;
    }

    clear() {
        this.entityCollection.forEach(entity => {
            this.viewer.entities.remove(entity);
        });
        this.labelEntityCollection.forEach(entity => {
            this.viewer.entities.remove(entity);
        });
        this.totalEntityCollection.forEach(entity => {
            this.viewer.entities.remove(entity);
        });
        this.entityCollection = [];
        this.labelEntityCollection = [];
        this.totalEntityCollection = [];
    }

    /**
     * 清除
     */
    destroy(callback) {
        if (this.startTipEntity) {
            this.viewer.entities.remove(this.startTipEntity);
        }
        this.entityCollection;
        this.viewer.screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.viewer.screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.viewer.screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        this.viewer.screenSpaceEventHandler.removeInputAction(
            ScreenSpaceEventType.LEFT_DOUBLE_CLICK,
        );
        this.viewer.cancelBubble = false;
        this.startDraw = false;
        this.clear();
        if (callback) {
            callback();
        }
    }

    visibleLabelEntity(visible) {
        this.labelEntityCollection.forEach(entity => {
            entity.show = visible;
        });
        this.labelVisible = visible;
    }

    removeStartTip() {
        if (this.startTipEntity) {
            this.viewer.entities.remove(this.startTipEntity);
        }
    }

    startDrawTip(movePosition) {
        if (!this.startDraw) {
            this.removeStartTip();
            // 计算距离
            let lengthText = this.TextEnum.messureStartTipText;

            this.startTipEntity = this.addLabel(movePosition, lengthText);
        }
    }

    /**
     * 测距
     */
    measurePolyLine(callback) {
        let positions = [];
        let labelEntity = null; // 标签实体
        let totalLabelEntity = null;
        let positionsList = [];
        this.viewer.cancelBubble = true;
        this.startDrawTip();
        this.viewer.screenSpaceEventHandler.setInputAction(moveEvent => {
            let movePosition = this.viewer.scene.pickPosition(moveEvent.endPosition); // 鼠标移动的点
            // var movePosition = this.viewer.scene.globe.pick(this.viewer.camera.getPickRay(moveEvent.endPosition), this.viewer.scene);

            if (!movePosition) {
                return false;
            }
            this.startDrawTip(movePosition);
            if (this.startDraw) {
                if (positions.length == 2) {
                    positions.pop();
                    positions.push(movePosition);
                    // 绘制label
                    if (labelEntity) {
                        this.viewer.entities.remove(labelEntity);
                        this.labelEntityCollection.splice(
                            this.labelEntityCollection.indexOf(labelEntity),
                            1,
                        );
                    }
                    if (totalLabelEntity) {
                        this.viewer.entities.remove(totalLabelEntity);
                        this.totalEntityCollection.splice(
                            this.totalEntityCollection.indexOf(totalLabelEntity),
                            1,
                        );
                    }
                    // 计算中点
                    let centerPoint = Cartesian3.midpoint(
                        positions[0],
                        positions[1],
                        new Cartesian3(),
                    );
                    const currentLength = this.getLengthText(positions[0], positions[1]);
                    let totalLength = Number(currentLength);
                    positionsList.forEach(positions => {
                        totalLength += Number(this.getLengthText(positions[0], positions[1]));
                    });
                    // 计算距离
                    let lengthText = `${this.TextEnum.distanceText}：` + currentLength + 'km';

                    labelEntity = this.addLabel(centerPoint, lengthText);
                    labelEntity.show = this.labelVisible;
                    totalLabelEntity = this.addLabel(
                        movePosition,
                        `${this.TextEnum.totalDistanceText}：` + totalLength.toFixed(2) + 'km',
                    );
                    this.labelEntityCollection.push(labelEntity);
                    this.totalEntityCollection.push(totalLabelEntity);
                } else {
                    positions.push(movePosition);

                    // 绘制线
                    this.addLine(positions);
                }
            }
        }, ScreenSpaceEventType.MOUSE_MOVE);
        // 注册鼠标左击事件
        this.viewer.screenSpaceEventHandler.setInputAction(clickEvent => {
            let cartesian = this.viewer.scene.pickPosition(clickEvent.position); // 坐标
            // var cartesian = this.viewer.scene.globe.pick(this.viewer.camera.getPickRay(clickEvent.position), this.viewer.scene);
            // 存储第一个点
            if (!cartesian) {
                return false;
            }
            if (!this.startDraw) {
                if (this.clearLast) {
                    this.clear();
                }
                this.startDraw = true;
            }
            this.removeStartTip();
            if (positions.length == 0) {
                positions.push(cartesian.clone());

                this.addPoint(cartesian);
                // 注册鼠标移动事件
            } else {
                // 存储第二个点
                positions.pop();
                positions.push(cartesian);
                this.addPoint(cartesian);
                this.addLine([...positions]);
                positionsList.push([...positions]);
                positions = [cartesian];
                this.viewer.cancelBubble = false;
                labelEntity = null;
                totalLabelEntity = null;
                if (callback) {
                    callback();
                }
            }
        }, ScreenSpaceEventType.LEFT_CLICK);
        this.viewer.screenSpaceEventHandler.setInputAction(() => {
            this.startDraw = false;
            positions = [];
            positionsList = [];
            labelEntity = null;
            totalLabelEntity = null;
            if (callback) {
                callback();
            }
        }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    }

    /**
     * 测面积
     */
    measurePolygon(callback) {
        let positions = [];
        let clickStatus = false;
        let labelEntityCollection = [];
        let totalLabelEntity = null;
        this.viewer.scene.globe.depthTestAgainstTerrain = false;
        this.viewer.cancelBubble = true;
        this.viewer.screenSpaceEventHandler.setInputAction(moveEvent => {
            // var movePosition = this.viewer.scene.pickPosition(moveEvent.endPosition);
            let movePosition = this.viewer.scene.globe.pick(
                this.viewer.camera.getPickRay(moveEvent.endPosition),
                this.viewer.scene,
            );
            // console.log(movePosition);
            if (!movePosition) {
                return false;
            }
            if (!this.startDraw) {
                return this.startDrawTip(movePosition);
            }
            if (positions.length == 1) {
                positions.push(movePosition);
                this.addLine(positions);
            } else {
                if (clickStatus) {
                    positions.push(movePosition);
                } else {
                    positions.pop();
                    positions.push(movePosition);
                }
            }

            if (positions.length >= 3) {
                // 绘制label
                if (totalLabelEntity) {
                    this.viewer.entities.remove(totalLabelEntity);
                    this.totalEntityCollection.splice(
                        this.totalEntityCollection.indexOf(totalLabelEntity),
                        1,
                    );
                }

                let text = `${this.TextEnum.areaText}：` + this.getArea(positions) + 'km²';
                let centerPoint = this.getCenterOfGravityPoint(positions);
                totalLabelEntity = this.addLabel(centerPoint, text);

                this.totalEntityCollection.push(totalLabelEntity);
            }
            if (positions.length >= 2) {
                labelEntityCollection.forEach(entity => {
                    this.viewer.entities.remove(entity);
                    this.labelEntityCollection.splice(
                        this.labelEntityCollection.indexOf(entity),
                        1,
                    );
                });
                positions.forEach((position, index) => {
                    if (index === positions.length - 1) {
                        return;
                    }
                    const prevPosition = positions[index + 1];
                    let centerPoint = Cartesian3.midpoint(position, prevPosition, new Cartesian3());
                    const currentLength = this.getLengthText(position, prevPosition);
                    // 计算距离
                    let lengthText = `${this.TextEnum.distanceText}：` + currentLength + 'km';

                    const labelEntity = this.addLabel(centerPoint, lengthText);
                    labelEntity.show = this.labelVisible;
                    labelEntityCollection.push(labelEntity);
                });
                this.labelEntityCollection.push(...labelEntityCollection);
            }

            clickStatus = false;
        }, ScreenSpaceEventType.MOUSE_MOVE);
        this.viewer.screenSpaceEventHandler.setInputAction(clickEvent => {
            clickStatus = true;
            if (!this.startDraw) {
                this.startDraw = true;
                if (this.clearLast) {
                    this.clear();
                }
            }

            // var cartesian = this.viewer.scene.pickPosition(clickEvent.position);
            let cartesian = this.viewer.scene.globe.pick(
                this.viewer.camera.getPickRay(clickEvent.position),
                this.viewer.scene,
            );
            // console.log(cartesian);

            if (!cartesian) {
                return false;
            }
            if (positions.length == 0) {
                positions.push(cartesian.clone()); //鼠标左击 添加第1个点
                this.addPoint(cartesian);
            } else if (positions.length == 2) {
                if (!cartesian) {
                    return false;
                }
                positions.pop();
                positions.push(cartesian.clone()); // 鼠标左击 添加第2个点

                this.addPoint(cartesian);

                this.addPolyGon(positions);

                // 右击结束
                this.viewer.screenSpaceEventHandler.setInputAction(clickEvent => {
                    // var clickPosition = this.viewer.scene.pickPosition(clickEvent.position);
                    let clickPosition = this.viewer.scene.globe.pick(
                        this.viewer.camera.getPickRay(clickEvent.position),
                        this.viewer.scene,
                    );
                    // console.log(clickPosition);
                    if (!clickPosition) {
                        return false;
                    }
                    positions.pop();
                    positions.push(clickPosition);
                    positions.push(positions[0]); // 闭合
                    this.addPoint(clickPosition);
                    positions = [];
                    this.viewer.cancelBubble = false;
                    this.startDraw = false;
                    totalLabelEntity = null;
                    labelEntityCollection = [];
                    if (callback) {
                        callback();
                    }
                }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
            } else if (positions.length >= 3) {
                if (!cartesian) {
                    return false;
                }
                positions.pop();
                positions.push(cartesian.clone()); // 鼠标左击 添加第3个点
                this.addPoint(cartesian);
            }
        }, ScreenSpaceEventType.LEFT_CLICK);
    }

    /**
     * 测高
     */
    measureHeight(callback) {
        let positions = [];
        let labelEntity_1 = null; // 标签实体
        let labelEntity_2 = null; // 标签实体
        let labelEntity_3 = null; // 标签实体

        // 注册鼠标左击事件
        this.viewer.screenSpaceEventHandler.setInputAction(clickEvent => {
            let cartesian = this.viewer.scene.pickPosition(clickEvent.position); // 坐标
            //var cartesian = this.viewer.scene.globe.pick(this.viewer.camera.getPickRay(clickEvent.position), this.viewer.scene);

            // 存储第一个点

            if (positions.length == 0) {
                if (!cartesian) {
                    return false;
                }
                positions.push(cartesian.clone());
                this.addPoint(cartesian);

                // 注册鼠标移动事件
                this.viewer.screenSpaceEventHandler.setInputAction(moveEvent => {
                    let movePosition = this.viewer.scene.pickPosition(moveEvent.endPosition); // 鼠标移动的点
                    //var movePosition = this.viewer.scene.globe.pick(this.viewer.camera.getPickRay(moveEvent.endPosition), this.viewer.scene);
                    if (!movePosition) {
                        return false;
                    }
                    if (positions.length >= 2) {
                        positions.pop();
                        positions.pop();
                        positions.pop();

                        let cartographic = Cartographic.fromCartesian(movePosition);
                        let height = Cartographic.fromCartesian(positions[0]).height;

                        const verticalPoint = Cartesian3.fromDegrees(
                            Math.toDegrees(cartographic.longitude),
                            Math.toDegrees(cartographic.latitude),
                            height,
                        );
                        positions.push(verticalPoint);
                        positions.push(movePosition);
                        positions.push(positions[0]);

                        // 绘制label
                        if (labelEntity_1) {
                            this.viewer.entities.remove(labelEntity_1);
                            this.entityCollection.splice(
                                this.entityCollection.indexOf(labelEntity_1),
                                1,
                            );
                            this.viewer.entities.remove(labelEntity_2);
                            this.entityCollection.splice(
                                this.entityCollection.indexOf(labelEntity_2),
                                1,
                            );
                            this.viewer.entities.remove(labelEntity_3);
                            this.entityCollection.splice(
                                this.entityCollection.indexOf(labelEntity_3),
                                1,
                            );
                        }

                        // 计算中点
                        let centerPoint_1 = Cartesian3.midpoint(
                            positions[0],
                            positions[1],
                            new Cartesian3(),
                        );
                        // 计算距离
                        let lengthText_1 =
                            `${this.TextEnum.horizontalDistanceText}：` +
                            this.getLengthText(positions[0], positions[1]);

                        labelEntity_1 = this.addLabel(centerPoint_1, lengthText_1);
                        this.entityCollection.push(labelEntity_1);

                        // 计算中点
                        let centerPoint_2 = Cartesian3.midpoint(
                            positions[1],
                            positions[2],
                            new Cartesian3(),
                        );
                        // 计算距离
                        let lengthText_2 =
                            `${this.TextEnum.verticalDistanceText}：` +
                            this.getLengthText(positions[1], positions[2]);

                        labelEntity_2 = this.addLabel(centerPoint_2, lengthText_2);
                        this.entityCollection.push(labelEntity_2);

                        // 计算中点
                        let centerPoint_3 = Cartesian3.midpoint(
                            positions[2],
                            positions[3],
                            new Cartesian3(),
                        );
                        // 计算距离
                        let lengthText_3 =
                            `${this.TextEnum.straightLineDistanceText}：` +
                            this.getLengthText(positions[2], positions[3]);

                        labelEntity_3 = this.addLabel(centerPoint_3, lengthText_3);
                        this.entityCollection.push(labelEntity_3);
                    } else {
                        const verticalPoint = new Cartesian3(
                            movePosition.x,
                            movePosition.y,
                            positions[0].z,
                        );
                        positions.push(verticalPoint);
                        positions.push(movePosition);
                        positions.push(positions[0]);
                        // 绘制线
                        this.addLine(positions);
                    }
                }, ScreenSpaceEventType.MOUSE_MOVE);
            } else {
                // 存储第二个点
                positions.pop();
                positions.pop();
                positions.pop();
                let cartographic = Cartographic.fromCartesian(cartesian);
                let height = Cartographic.fromCartesian(positions[0]).height;

                let verticalPoint = Cartesian3.fromDegrees(
                    Math.toDegrees(cartographic.longitude),
                    Math.toDegrees(cartographic.latitude),
                    height,
                );
                positions.push(verticalPoint);
                positions.push(cartesian);
                positions.push(positions[0]);
                this.addPoint(cartesian);
                this.viewer.screenSpaceEventHandler.removeInputAction(
                    ScreenSpaceEventType.LEFT_CLICK,
                );
                this.viewer.screenSpaceEventHandler.removeInputAction(
                    ScreenSpaceEventType.MOUSE_MOVE,
                );

                if (callback) {
                    callback();
                }
            }
        }, ScreenSpaceEventType.LEFT_CLICK);
    }

    /**
     * 添加点
     * @param position
     */
    addPoint(position) {
        this.entityCollection.push(
            this.viewer.entities.add(
                new Entity({
                    cancelPopup: true,
                    position: position,
                    point: {
                        color: Color.BLUE,
                        pixelSize: 5,
                        heightReference: HeightReference.NONE,
                    },
                }),
            ),
        );
    }

    /**
     * 添加线
     * @param positions
     */
    addLine(positions) {
        let dynamicPositions = new CallbackProperty(() => {
            return positions;
        }, false);
        this.entityCollection.push(
            this.viewer.entities.add(
                new Entity({
                    cancelPopup: true,
                    polyline: {
                        positions: dynamicPositions,
                        width: 2,
                        arcType: ArcType.RHUMB,
                        clampToGround: true,
                        material: Color.RED, //获取或设置折线的表面外观
                        heightReference: HeightReference.RELATIVE_TO_GROUND,
                    },
                }),
            ),
        );
    }

    /**
     * 添加面
     * @param positions
     */
    addPolyGon(positions) {
        let dynamicPositions = new CallbackProperty(() => {
            return new PolygonHierarchy(positions);
        }, false);
        this.entityCollection.push(
            this.viewer.entities.add(
                new Entity({
                    cancelPopup: true,
                    polygon: {
                        hierarchy: dynamicPositions,
                        material: Color.RED.withAlpha(0.6),
                        classificationType: ClassificationType.BOTH, // 贴地表和贴模型,如果设置了，这不能使用挤出高度
                    },
                }),
            ),
        );
    }

    /**
     * 添加标签
     * @param position
     * @param text
     */
    addLabel(centerPoint, text) {
        return this.viewer.entities.add(
            new Entity({
                cancelPopup: true,
                position: centerPoint,
                label: {
                    text: text,
                    font: '14px sans-serif',
                    style: LabelStyle.FILL_AND_OUTLINE, //FILL  FILL_AND_OUTLINE OUTLINE
                    fillColor: Color.YELLOW,
                    showBackground: true, //指定标签后面背景的可见性
                    backgroundColor: new Color(0.165, 0.165, 0.165, 0.8), // 背景颜色
                    backgroundPadding: new Cartesian2(6, 6), //指定以像素为单位的水平和垂直背景填充padding
                    pixelOffset: new Cartesian2(0, -25),
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                },
            }),
        );
    }

    /**
     * 计算两点距离
     * @param firstPoint
     * @param secondPoint
     */
    getLengthText(firstPoint, secondPoint) {
        // 计算距离
        let length = Cartesian3.distance(firstPoint, secondPoint);
        return (length / 1000).toFixed(2);
    }

    getArea(points) {
        let ps = points.map(point => {
            let cartographic = Ellipsoid.WGS84.cartesianToCartographic(point);
            let height = this.viewer.scene.globe.getHeight(cartographic);
            return Cartesian3.fromDegrees(
                (cartographic.longitude / Math.PI) * 180,
                (cartographic.latitude / Math.PI) * 180,
                height,
            );
        });
        let s = 0;
        for (let i = 0; i < ps.length; i++) {
            let p1 = ps[i];
            let p2;
            if (i < ps.length - 1) p2 = ps[i + 1];
            else p2 = ps[0];
            s += p1.x * p2.y - p2.x * p1.y;
        }

        return Math.abs((s / 1000000.0).toFixed(4));
    }

    /**
     * 计算多边形的中心（简单的处理）
     * @param mPoints
     * @returns {*[]}
     */
    getCenterOfGravityPoint(mPoints) {
        let centerPoint = mPoints[0];
        for (let i = 1; i < mPoints.length; i++) {
            centerPoint = Cartesian3.midpoint(centerPoint, mPoints[i], new Cartesian3());
        }
        return centerPoint;
    }
}
